import { useRef, useState, useEffect } from 'react';
import BubbleContainer from './_components/Bubble/BubbleContainer';
import Input from '@/components/atoms/Input';
import { cn } from '@/utils/tailwind';
import BotBubble from './_components/Bubble/BotBubble';
import { cplatApiV2Sellkey } from '@/api';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@/redux/store';
import { useToast } from '@/hooks/useToast';
import { Message, ProductName } from '@/types/discover';
import useTextareaHeight from '@/hooks/V3/useTextareaHeight';
import useFilteredMessages from '@/hooks/V3/useFilteredMessage';
import { logEvent } from '@/etc/firebase';
import { useChatbotViewport } from '@/hooks/V3/useChatbotViewport';
import { useLog } from '@/hooks/V3/useLog';
import { useRecoilValue } from 'recoil';
import { deviceUuidState } from '@/store/atom';
import { useSafeAreaSpacing } from '@/hooks/useSafeAreaSpacing';

export const ChangeNamePage = () => {
  const [searchParams] = useSearchParams();
  const cplatToken = searchParams.get('cplatToken');

  const user = useAppSelector((state) => state.user);

  /* 모바일 키보드 활성화에 따른 스크롤 영역 height 값 계산 */
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { textareaHeight, adjustHeight } = useTextareaHeight(inputRef, scrollDivRef);
  useChatbotViewport(scrollDivRef, textareaHeight);
  ////////////////////////////////

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { messages, setMessages, setStoredMessages } = useFilteredMessages(
    `change_name_messages_${user.UserNo}`,
    {
      id: 0,
      text: '도매몰 또는 해외 상품명을 붙여넣어보세요.\nAI가 알맞은 상품명으로 바꿔드려요.',
      sender: 'bot',
    },
  );

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isActive = !isLoading && input.trim();
  const { toast } = useToast();
  const [productNameListData, setProductNameListData] = useState<Array<ProductName>>([]);

  const botMessages = messages.filter((message) => message.sender === 'bot');
  const latestBotMessageId =
    botMessages.length > 0 ? Math.max(...botMessages.map((msg) => msg.id)) : null;

  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);
  const { getBottomSpacing } = useSafeAreaSpacing();

  const getProductName = async () => {
    const chatMessage = input.replace('\n', '');

    try {
      setIsLoading(true);
      const {
        data: { code, data },
      } = await cplatApiV2Sellkey.post('sellkey/get_optimization_product_name', {
        CplatToken: cplatToken || user.CplatToken,
        ProductName: chatMessage,
      });

      if (code === '200') {
        const { ProductNameList } = data[0];
        setProductNameListData(ProductNameList);
        const botMessage: Message = {
          id: Date.now() + 1,
          text: ProductNameList[0].product_name,
          sender: 'bot',
          subText: ProductNameList[0].Consideration,
          list: ProductNameList[0].Keywords,
          remainCount: 4,
        };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
        logEvent('ai_change_name', {
          status: code,
          user_id: user.UserNo,
          input: chatMessage,
          optimized_product_name: ProductNameList[0].product_name,
          keywords: ProductNameList[0].Keywords.join(', '),
        });
      } else {
        if (inputRef.current) {
          inputRef.current.blur();
        }
        toast({
          icon: '⚠️',
          title: '일시적으로 오류가 발생하였습니다.\n오류가 반복되면 1:1 문의를 남겨주세요.',
          variant: 'dark',
        });
        logEvent('ai_change_name', {
          user_id: user.UserNo,
          input: chatMessage,
          status: code,
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (isActive) {
      const newMessage: Message = { id: Date.now(), text: input, sender: 'user' };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      setInput('');
      getProductName();

      setUserEventHistory({
        eventName: 'change_ai_product_name',
        eventValue: input,
        deviceUuid,
      });
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const cleanedInput = e.target.value.replace(/ {2,}/g, ' ');
    setInput(cleanedInput);
  };

  useEffect(() => {
    inputRef.current && adjustHeight();
  }, [input]);

  // 메시지 추가 시
  useEffect(() => {
    // 로컬 스토리지에 저장
    setStoredMessages(messages);

    // 스크롤을 최하단으로 이동
    const isUserMessage = messages[messages.length - 1]?.sender === 'user';
    const scrollBehavior = isUserMessage ? 'instant' : 'smooth';
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: scrollBehavior });
    }
  }, [messages]);

  useEffect(() => {
    if (isLoading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isLoading]);

  return (
    <div className="flex h-screen w-full flex-col bg-white">
      <div
        className="overflow-auto border-b-[1px] border-gr-100 pb-[10px] pl-[16px] pr-[12px] pt-[16px]"
        ref={scrollDivRef}
      >
        {messages.map((message) => (
          <BubbleContainer
            key={message.id}
            data={message}
            messages={messages}
            setMessages={setMessages}
            setIsLoading={setIsLoading}
            productNameList={productNameListData}
            isLatest={message.sender === 'bot' && message.id === latestBotMessageId}
            actionType="default"
            isGradient
          />
        ))}
        <div ref={messagesEndRef} />
        {isLoading && <BotBubble type="loading" isGradient />}
      </div>

      <div
        className="flex pl-[16px] pr-[12px] pt-[10px]"
        style={{ paddingBottom: getBottomSpacing() }}
      >
        <Input
          textarea={true}
          ref={inputRef}
          className="focous:border-none Body5M14 resize-none overflow-y-auto rounded-[8px] border-none bg-gr-100 py-[10px] pr-[70px] scrollbar-hide"
          value={input}
          onChange={handleInput}
          placeholder="최적화할 상품명을 입력해 주세요"
          InputProps={{
            endAdornment: (
              <button
                onClick={handleSendMessage}
                className={cn(
                  'rounded-[8px]  bg-point-500 px-[8px] py-[5px] text-white Caption1M12',
                  !isActive && 'bg-gr-300',
                )}
              >
                만들기
              </button>
            ),
            endAdornmentStyle: 'right-[8px] absolute m-auto whitespace-nowrap bottom-[7px]',
          }}
          maxLength={100}
        />
      </div>
    </div>
  );
};
